import { Link } from "@/lib/router-compat";
import { jsonLd } from "@/lib/seoHelpers";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { productHubs } from "@/lib/productHubConfig";
import { BreadcrumbSchema } from "@/components/SEO/BreadcrumbSchema";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronRight, Star, BookOpen, ArrowRight, Volume2, Truck, SprayCan, Wrench, Droplets, Factory, Car, Home, Zap, Battery, Fuel, Hammer, Scale, ShieldCheck } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { generatorSubcategoryList } from "@/lib/generatorSubcategoryConfig";
import { lawnMowerSubcategoryList } from "@/lib/lawnMowerSubcategoryConfig";
import { chainsawSubcategoryList } from "@/lib/chainsawSubcategoryConfig";
import { airCompressorSubcategoryList, airCompressorGroupLabels, airCompressorGroupOrder } from "@/lib/airCompressorSubcategoryConfig";
import { pressureWasherSubcategoryList, pressureWasherGroupLabels, pressureWasherGroupOrder } from "@/lib/pressureWasherSubcategoryConfig";
import HubTopProducts from "@/components/HubTopProducts";
import { ProductRatingBadge } from "@/components/ProductRatingBadge";
import NotFound from "./NotFound";
import { hubArticlesQueryOptions, MIN_HUB_ARTICLES } from "@/lib/hubArticles";
import { getSearchDescription } from "@/lib/searchDescriptions";

const BASE_URL = "https://www.garden-review.co.uk";

interface ProductHubProps {
  hubSlug: string;
}

const ProductHub = ({ hubSlug }: ProductHubProps) => {
  const config = productHubs[hubSlug];

  const { data: allArticles = [], isLoading } = useQuery({
    ...hubArticlesQueryOptions(hubSlug),
    enabled: !!config,
  });
  const articles = allArticles.slice(0, 9);
  const isThinHub = !isLoading && allArticles.length < MIN_HUB_ARTICLES;

  if (!config) return <NotFound />;

  const canonicalUrl = `${BASE_URL}/${config.slug}`;
  const metaDescription = getSearchDescription(`/${config.slug}`, config.metaDescription);

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: config.title,
    description: metaDescription,
    url: canonicalUrl,
    isPartOf: { "@type": "WebSite", name: "Garden Review", url: BASE_URL },
  };

  // Hedge-trimmer FAQ schema (AEO) — only emitted on the hedge-trimmers hub
  const hedgeTrimmerFaqs = hubSlug === "hedge-trimmers" ? [
    { q: "What is the best type of hedge trimmer for a small UK garden?", a: "For most small UK gardens a cordless (battery) hedge trimmer with a 45–50cm blade is the sweet spot — light enough to use one-handed on box or privet, quiet enough for early-morning use, and cordless so you're not dragging a cable through the flower beds." },
    { q: "How do cordless, corded and petrol hedge trimmers compare?", a: "Corded electric trimmers are the cheapest and lightest but limited by cable reach. Cordless (18V–40V) trimmers offer the best balance of power and freedom for domestic hedges. Petrol trimmers deliver the most power and runtime for long or thick hedges but are heavier, louder and need fuel mixing." },
    { q: "What blade length do I need on a hedge trimmer?", a: "40–45cm suits small ornamental hedges and topiary. 50–55cm is the domestic sweet spot for privet, laurel and beech. 60cm+ speeds up work on long runs of leylandii or conifer but the trimmer becomes heavier and harder to control on detailed work." },
    { q: "Do I need a long-reach hedge trimmer?", a: "A long-reach (or telescopic) hedge trimmer is worth it if you have hedges over about 2m or need to trim the tops of tall runs without a ladder. For most homeowners a standard trimmer plus a stable step platform is safer and cheaper." },
    { q: "What's the best cheap hedge trimmer in the UK?", a: "For budget buyers the Bosch AHS 50-16, Black+Decker BEHTS301 and Terratek TCHT-4 corded trimmers consistently review well under £70. If you want cordless, look at the Ryobi ONE+ 18V bare tool if you already own the batteries." },
  ] : null;

  const logSplitterFaqs = hubSlug === "log-splitters" ? [
    { q: "What size log splitter do I need?", a: "For occasional domestic firewood, a 4–6 tonne electric splitter is often enough for straight-grained, seasoned logs. Larger, knotty or green hardwood may need 7 tonnes or more. Treat the maker's tonnage figure as a guide and check its stated maximum log diameter and length too." },
    { q: "Are electric log splitters any good?", a: "Electric log splitters suit most UK homes because they are quieter, lower-maintenance and easier to store than petrol machines. Their limits are mains access and lower output when working through very large, knotty or green rounds." },
    { q: "Is a vertical or horizontal log splitter better?", a: "Horizontal models are compact and convenient for manageable logs that can be lifted onto the bed. Vertical machines reduce heavy lifting and usually suit larger rounds, but cost more and require more storage space." },
    { q: "Should I hire or buy a log splitter?", a: "Hiring can make sense for a single large pile and avoids maintenance and storage. Buying is usually more convenient when you process firewood several times a year, provided the machine matches your normal log size and you can store it safely." },
  ] : null;

  const faqJsonLd = hedgeTrimmerFaqs ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: hedgeTrimmerFaqs.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null;

  const logSplitterFaqJsonLd = logSplitterFaqs ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: logSplitterFaqs.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null;

  return (
    <>
      <Helmet>
        <title>{config.metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={config.metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
        {isThinHub && <meta name="robots" content="noindex,follow" />}
        <script type="application/ld+json">{jsonLd(pageSchema)}</script>
        {faqJsonLd && (
          <script type="application/ld+json">{jsonLd(faqJsonLd)}</script>
        )}
        {logSplitterFaqJsonLd && (
          <script type="application/ld+json">{jsonLd(logSplitterFaqJsonLd)}</script>
        )}
      </Helmet>
      <BreadcrumbSchema items={[
        { name: "Home", url: BASE_URL },
        { name: "Reviews & Buying Guides", url: `${BASE_URL}/category/reviews` },
        { name: config.title, url: canonicalUrl },
      ]} />

      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero — skinny single-row banner */}
        <section className={cn("relative overflow-hidden border-b py-2 md:py-2.5", config.heroGradient)}>
          <div className="relative z-10 container mx-auto px-4 lg:px-8 max-w-5xl">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1.5 md:gap-4 py-1">
              {/* Breadcrumb (left) */}
              <nav className="flex items-center gap-2 text-xs text-muted-foreground min-w-0" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-foreground transition-colors whitespace-nowrap">Home</Link>
                <ChevronRight className="h-3 w-3 shrink-0" />
                <Link to="/category/reviews" className="hover:text-foreground transition-colors whitespace-nowrap">Reviews & Buying Guides</Link>
                <ChevronRight className="h-3 w-3 shrink-0" />
                <span className="text-foreground font-medium whitespace-nowrap">{config.title}</span>
              </nav>
              {/* Title + description (right, inline with breadcrumb) */}
              <div className="flex items-center gap-2.5 md:justify-end min-w-0">
                <h1 className="text-lg md:text-xl font-bold text-foreground whitespace-nowrap leading-tight">
                  {config.title}
                </h1>
                <span className="hidden md:block text-foreground/30" aria-hidden="true">·</span>
                <p className="hidden md:block text-sm text-foreground/75 line-clamp-1 max-w-md">
                  UK Reviews & Buying Guide
                </p>
              </div>
            </div>
          </div>
        </section>

        {hubSlug === "log-splitters" && (
          <section className="border-b bg-card" aria-labelledby="log-splitter-chooser">
            <div className="container mx-auto max-w-5xl px-4 py-8 lg:px-8">
              <h2 id="log-splitter-chooser" className="text-2xl font-bold text-foreground">Choose the right type of log splitter</h2>
              <p className="mt-2 max-w-3xl text-muted-foreground">Start with where you will work, the logs you process and how often you split them. These routes cover the main choices without pushing every buyer towards the largest machine.</p>
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  { title: "Electric", copy: "Quiet, low-maintenance machines for homes with nearby mains power.", href: "/log-splitters/buying-guides", Icon: Zap },
                  { title: "Petrol", copy: "Mobile, higher-output splitters for remote sites and heavy workloads.", href: "/log-splitters/buying-guides", Icon: Fuel },
                  { title: "Manual", copy: "Simple, compact options for kindling and occasional small batches.", href: "/log-splitters/buying-guides", Icon: Hammer },
                  { title: "Hire or buy", copy: "Work out which offers better value for your volume and storage space.", href: "/log-splitters/buying-guides", Icon: Scale },
                ].map(({ title, copy, href, Icon }) => (
                  <Link key={title} to={href} className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary">
                    <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
                    <h3 className="mt-3 font-semibold text-foreground group-hover:text-primary">{title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{copy}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Generator subcategory submenu */}
        {hubSlug === "generators" && (
          <nav aria-label="Generator categories" className="border-b bg-card sticky top-0 z-30">
            <div className="container mx-auto px-4">
              <div className="flex items-center gap-2 overflow-x-auto py-3 text-sm scrollbar-hide">
                <span className="text-muted-foreground font-medium shrink-0 mr-2">Browse by category:</span>
                {generatorSubcategoryList.map(sc => (
                  <Link
                    key={sc.slug}
                    to={`/generators/category/${sc.slug}`}
                    className="shrink-0 px-3 py-1 rounded-full border border-border hover:border-primary/50 hover:bg-primary/5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {sc.title}
                  </Link>
                ))}
                <Link
                  to="/best-generators-uk"
                  className="shrink-0 px-3 py-1 rounded-full border border-primary/40 bg-primary/5 hover:bg-primary/10 text-xs font-semibold text-primary transition-colors"
                >
                  Interactive Buyer's Guide
                </Link>
              </div>
            </div>
          </nav>
        )}

        {/* Chainsaw subcategory submenu */}
        {hubSlug === "chainsaws" && (
          <nav aria-label="Chainsaw categories" className="border-b bg-card sticky top-0 z-30">
            <div className="container mx-auto px-4">
              <div className="flex items-center gap-2 overflow-x-auto py-3 text-sm scrollbar-hide">
                <span className="text-muted-foreground font-medium shrink-0 mr-2">Browse by category:</span>
                {chainsawSubcategoryList.map(sc => (
                  <Link
                    key={sc.slug}
                    to={`/chainsaws/category/${sc.slug}`}
                    className="shrink-0 px-3 py-1 rounded-full border border-border hover:border-primary/50 hover:bg-primary/5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {sc.title}
                  </Link>
                ))}
                <Link
                  to="/best-chainsaws-uk"
                  className="shrink-0 px-3 py-1 rounded-full border border-primary/40 bg-primary/5 hover:bg-primary/10 text-xs font-semibold text-primary transition-colors"
                >
                  Interactive Buyer's Guide
                </Link>
              </div>
            </div>
          </nav>
        )}

        {/* Lawn mower subcategory submenu */}
        {hubSlug === "lawn-mowers" && (
          <nav aria-label="Lawn mower categories" className="border-b bg-card sticky top-0 z-30">
            <div className="container mx-auto px-4">
              <div className="flex items-center gap-2 overflow-x-auto py-3 text-sm scrollbar-hide">
                <span className="text-muted-foreground font-medium shrink-0 mr-2">Browse by category:</span>
                {lawnMowerSubcategoryList.map(sc => (
                  <Link
                    key={sc.slug}
                    to={`/lawn-mowers/category/${sc.slug}`}
                    className="shrink-0 px-3 py-1 rounded-full border border-border hover:border-primary/50 hover:bg-primary/5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {sc.title}
                  </Link>
                ))}
                <Link
                  to="/best-lawn-mowers-uk"
                  className="shrink-0 px-3 py-1 rounded-full border border-primary/40 bg-primary/5 hover:bg-primary/10 text-xs font-semibold text-primary transition-colors"
                >
                  Interactive Buyer's Guide
                </Link>
              </div>
            </div>
          </nav>
        )}

        {/* Air compressor: clean funnel — Featured picks + Tabs */}
        {hubSlug === "air-compressors" && (() => {
          const featured: { slug: string; label: string; tagline: string; Icon: typeof Volume2; bg: string; iconBg: string; iconColor: string }[] = [
            { slug: "silent-air-compressors", label: "Silent", tagline: "Quiet for home & indoor use", Icon: Volume2, bg: "bg-sky-50 hover:bg-sky-100 border-sky-200", iconBg: "bg-sky-100", iconColor: "text-sky-700" },
            { slug: "portable-air-compressors", label: "Portable", tagline: "Lightweight & mobile", Icon: Truck, bg: "bg-emerald-50 hover:bg-emerald-100 border-emerald-200", iconBg: "bg-emerald-100", iconColor: "text-emerald-700" },
            { slug: "air-compressors-for-spray-painting", label: "Spray Painting", tagline: "Steady CFM for finish work", Icon: SprayCan, bg: "bg-rose-50 hover:bg-rose-100 border-rose-200", iconBg: "bg-rose-100", iconColor: "text-rose-700" },
            { slug: "50l-air-compressors", label: "50L Tank", tagline: "DIY & light trade sweet spot", Icon: Droplets, bg: "bg-amber-50 hover:bg-amber-100 border-amber-200", iconBg: "bg-amber-100", iconColor: "text-amber-700" },
            { slug: "oil-free-air-compressors", label: "Oil-Free", tagline: "Low maintenance, clean air", Icon: Wrench, bg: "bg-violet-50 hover:bg-violet-100 border-violet-200", iconBg: "bg-violet-100", iconColor: "text-violet-700" },
            { slug: "air-compressors-for-home-workshops", label: "Home Workshop", tagline: "Best all-rounders for garages", Icon: Factory, bg: "bg-orange-50 hover:bg-orange-100 border-orange-200", iconBg: "bg-orange-100", iconColor: "text-orange-700" },
          ];
          return (
            <section aria-label="Shop air compressors by category" className="border-b bg-muted/30">
              <div className="container mx-auto px-4 py-8 max-w-6xl">
                <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground">Shop by category</h2>
                    <p className="text-sm text-muted-foreground">Find the right air compressor by type, use case, tank size or brand.</p>
                  </div>
                </div>

                {/* Featured picks */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                  {featured.map(f => (
                    <Link
                      key={f.slug}
                      to={`/air-compressors/category/${f.slug}`}
                      className={`group border rounded-lg p-3 transition-colors flex flex-col gap-2 ${f.bg}`}
                    >
                      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${f.iconBg}`}>
                        <f.Icon className={`h-5 w-5 ${f.iconColor}`} />
                      </div>
                      <div className="font-semibold text-sm text-foreground">{f.label}</div>
                      <div className="text-xs text-muted-foreground leading-snug">{f.tagline}</div>
                    </Link>
                  ))}
                </div>

                {/* Tabs for browsing the rest */}
                <Tabs defaultValue={airCompressorGroupOrder[0]} className="w-full">
                  <TabsList className="flex flex-wrap h-auto justify-start gap-1 bg-card border border-border p-1">
                    {airCompressorGroupOrder.map(g => (
                      <TabsTrigger key={g} value={g} className="text-xs">
                        {airCompressorGroupLabels[g].replace(/^By\s+/, "")}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {airCompressorGroupOrder.map(g => {
                    const items = airCompressorSubcategoryList.filter(sc => sc.group === g);
                    if (!items.length) return null;
                    return (
                      <TabsContent key={g} value={g} className="mt-4">
                        <div className="flex flex-wrap gap-2">
                          {items.map(sc => (
                            <Link
                              key={sc.slug}
                              to={`/air-compressors/category/${sc.slug}`}
                              className="px-3 py-1.5 rounded-md bg-card border border-border hover:border-primary hover:bg-primary/5 text-sm font-medium text-foreground transition-colors"
                            >
                              {sc.shortLabel}
                            </Link>
                          ))}
                        </div>
                      </TabsContent>
                    );
                  })}
                </Tabs>
              </div>
            </section>
          );
        })()}

        {/* Pressure washer: Shop by category — Featured picks + Tabs */}
        {hubSlug === "pressure-washers" && (() => {
          const featured: { slug: string; label: string; tagline: string; Icon: typeof Volume2; bg: string; iconBg: string; iconColor: string }[] = [
            { slug: "electric-pressure-washers", label: "Electric", tagline: "Quiet mains-powered cleaning", Icon: Zap, bg: "bg-sky-50 hover:bg-sky-100 border-sky-200", iconBg: "bg-sky-100", iconColor: "text-sky-700" },
            { slug: "petrol-pressure-washers", label: "Petrol", tagline: "Cordless power for big jobs", Icon: Factory, bg: "bg-orange-50 hover:bg-orange-100 border-orange-200", iconBg: "bg-orange-100", iconColor: "text-orange-700" },
            { slug: "cordless-pressure-washers", label: "Cordless", tagline: "Battery freedom, no hose tap", Icon: Battery, bg: "bg-emerald-50 hover:bg-emerald-100 border-emerald-200", iconBg: "bg-emerald-100", iconColor: "text-emerald-700" },
            { slug: "pressure-washers-for-cars", label: "Cars", tagline: "Safe pressure for paintwork", Icon: Car, bg: "bg-rose-50 hover:bg-rose-100 border-rose-200", iconBg: "bg-rose-100", iconColor: "text-rose-700" },
            { slug: "pressure-washers-for-patios", label: "Patios", tagline: "Lift moss, grime & algae", Icon: Droplets, bg: "bg-amber-50 hover:bg-amber-100 border-amber-200", iconBg: "bg-amber-100", iconColor: "text-amber-700" },
            { slug: "compact-pressure-washers", label: "Compact", tagline: "Easy to store & move", Icon: Home, bg: "bg-violet-50 hover:bg-violet-100 border-violet-200", iconBg: "bg-violet-100", iconColor: "text-violet-700" },
          ];
          return (
            <section aria-label="Shop pressure washers by category" className="border-b bg-muted/30">
              <div className="container mx-auto px-4 py-8 max-w-6xl">
                <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground">Shop by category</h2>
                    <p className="text-sm text-muted-foreground">Find the right pressure washer by type, use case, power source or brand.</p>
                  </div>
                </div>

                {/* Featured picks */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                  {featured.map(f => (
                    <Link
                      key={f.slug}
                      to={`/pressure-washers/category/${f.slug}`}
                      className={`group border rounded-lg p-3 transition-colors flex flex-col gap-2 ${f.bg}`}
                    >
                      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${f.iconBg}`}>
                        <f.Icon className={`h-5 w-5 ${f.iconColor}`} />
                      </div>
                      <div className="font-semibold text-sm text-foreground">{f.label}</div>
                      <div className="text-xs text-muted-foreground leading-snug">{f.tagline}</div>
                    </Link>
                  ))}
                </div>

                {/* Tabs for browsing the rest */}
                <Tabs defaultValue={pressureWasherGroupOrder[0]} className="w-full">
                  <TabsList className="flex flex-wrap h-auto justify-start gap-1 bg-card border border-border p-1">
                    {pressureWasherGroupOrder.map(g => (
                      <TabsTrigger key={g} value={g} className="text-xs">
                        {pressureWasherGroupLabels[g].replace(/^By\s+/, "")}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {pressureWasherGroupOrder.map(g => {
                    const items = pressureWasherSubcategoryList.filter(sc => sc.group === g);
                    if (!items.length) return null;
                    return (
                      <TabsContent key={g} value={g} className="mt-4">
                        <div className="flex flex-wrap gap-2">
                          {items.map(sc => (
                            <Link
                              key={sc.slug}
                              to={`/pressure-washers/category/${sc.slug}`}
                              className="px-3 py-1.5 rounded-md bg-card border border-border hover:border-primary hover:bg-primary/5 text-sm font-medium text-foreground transition-colors"
                            >
                              {sc.shortLabel}
                            </Link>
                          ))}
                        </div>
                      </TabsContent>
                    );
                  })}
                </Tabs>
              </div>
            </section>
          );
        })()}

        <main className="container mx-auto px-4 py-10 max-w-5xl">
          {/* Two CTAs */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <Link to={`/${config.slug}/reviews`} className="group">
              <Card className="h-full hover:shadow-lg transition-shadow border-2 hover:border-primary">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-md bg-amber-100 text-amber-700">
                      <Star className="h-5 w-5" />
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground">
                      {config.title} Reviews
                    </h2>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Independent, hands-on UK reviews of the latest {config.singular}s — ratings, pros, cons and the real-world performance you need to know.
                  </p>
                  <span className="inline-flex items-center text-primary font-semibold group-hover:underline">
                    Read all reviews <ArrowRight className="h-4 w-4 ml-1" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link to={`/${config.slug}/buying-guides`} className="group">
              <Card className="h-full hover:shadow-lg transition-shadow border-2 hover:border-primary">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-md bg-blue-100 text-blue-700">
                      <BookOpen className="h-5 w-5" />
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-foreground">
                      {config.title} Buying Guide
                    </h2>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Choosing your first or next {config.singular}? Our buying guides, comparisons and best-of lists explain exactly what to look for and which features actually matter.
                  </p>
                  <span className="inline-flex items-center text-primary font-semibold group-hover:underline">
                    Browse buying guides <ArrowRight className="h-4 w-4 ml-1" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </section>

          {/* Latest articles */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-6">
              Latest {config.title} Articles
            </h2>

            {isLoading && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-64 rounded-lg" />
                ))}
              </div>
            )}

            {!isLoading && articles.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map(article => (
                  <Link key={article.id} to={`/articles/${article.slug}`}>
                    <Card className="overflow-hidden hover:shadow-md transition-shadow h-full group">
                      {article.featured_image && (
                        <div className="relative">
                          <img
                            src={article.featured_image}
                            alt={article.featured_image_alt || article.title}
                            className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                      )}
                      <CardContent className="p-4 flex flex-col justify-between h-[calc(100%-10rem)]">
                        <div>
                          <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                            {article.title}
                          </h3>
                          {article.excerpt && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{article.excerpt}</p>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          {article.published_at && (
                            <p className="text-xs text-muted-foreground">
                              {new Date(article.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                            </p>
                          )}
                          <ProductRatingBadge rating={article.product_rating} />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}

            {!isLoading && articles.length === 0 && (
              <p className="text-muted-foreground">
                More {config.singular} content coming soon. In the meantime, browse our{" "}
                <Link to={`/${config.slug}/reviews`} className="text-primary hover:underline">reviews</Link>{" "}or our{" "}
                <Link to={`/${config.slug}/buying-guides`} className="text-primary hover:underline">buying guides</Link>.
              </p>
            )}
          </section>

          {hubSlug === "log-splitters" && (
            <>
              <section className="mt-16" aria-labelledby="log-splitter-types">
                <h2 id="log-splitter-types" className="text-2xl font-bold text-foreground">Electric, petrol or manual?</h2>
                <p className="mt-2 mb-6 max-w-3xl text-muted-foreground">The best power source depends on your workload and where the machine will be used. Compare the practical differences before focusing on tonnage.</p>
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full min-w-[680px] text-sm">
                    <thead className="bg-muted/50 text-left">
                      <tr>
                        <th className="p-3 font-semibold">Type</th>
                        <th className="p-3 font-semibold">Best suited to</th>
                        <th className="p-3 font-semibold">Typical force</th>
                        <th className="p-3 font-semibold">Mobility and noise</th>
                        <th className="p-3 font-semibold">Maintenance</th>
                      </tr>
                    </thead>
                    <tbody className="[&>tr:nth-child(even)]:bg-muted/20">
                      <tr><td className="p-3 font-semibold">Electric</td><td className="p-3">Domestic firewood and regular small batches</td><td className="p-3">About 4–8 tonnes</td><td className="p-3">Quiet, but needs suitable mains power</td><td className="p-3">Low</td></tr>
                      <tr><td className="p-3 font-semibold">Petrol</td><td className="p-3">Remote sites, large rounds and sustained work</td><td className="p-3">Often 7 tonnes and above</td><td className="p-3">Mobile, louder and heavier</td><td className="p-3">Engine and hydraulic upkeep</td></tr>
                      <tr><td className="p-3 font-semibold">Manual</td><td className="p-3">Kindling and occasional light use</td><td className="p-3">Varies by lever or hydraulic design</td><td className="p-3">Portable and quiet</td><td className="p-3">Very low</td></tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">Figures are broad manufacturer-stated ranges, not measured performance. Grain, knots, moisture and log diameter all affect the force required.</p>
              </section>

              <section className="mt-16 grid gap-6 md:grid-cols-2">
                <div>
                  <div className="flex items-center gap-2"><Scale className="h-5 w-5 text-primary" aria-hidden="true" /><h2 className="text-2xl font-bold text-foreground">What splitting force do you need?</h2></div>
                  <p className="mt-3 text-muted-foreground">A 4–6 tonne machine often covers straight-grained, seasoned domestic firewood. Step up when rounds are wider, greener, knotty or hardwood. Maximum log length and diameter matter as much as the headline tonnage.</p>
                  <Link to="/log-splitters/buying-guides" className="mt-4 inline-flex items-center font-semibold text-primary hover:underline">Read the sizing guides <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </div>
                <div>
                  <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" /><h2 className="text-2xl font-bold text-foreground">Use it safely</h2></div>
                  <p className="mt-3 text-muted-foreground">Set the splitter on stable ground, follow its two-hand control procedure, keep hands away from the wedge and wear suitable eye and foot protection. Never exceed the maker's log limits or modify a safety guard.</p>
                  <Link to="/log-splitters/buying-guides" className="mt-4 inline-flex items-center font-semibold text-primary hover:underline">Browse practical advice <ArrowRight className="ml-1 h-4 w-4" /></Link>
                </div>
              </section>

              <section className="mt-16" aria-labelledby="log-splitter-faqs">
                <h2 id="log-splitter-faqs" className="text-2xl font-bold text-foreground">Log splitter FAQs</h2>
                <div className="mt-6 space-y-6">
                  {logSplitterFaqs?.map((faq) => (
                    <div key={faq.q} className="border-l-4 border-primary/40 pl-4">
                      <h3 className="font-semibold text-foreground">{faq.q}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}

          {/* Hedge trimmer type comparison + FAQ (AEO content depth for /hedge-trimmers) */}
          {hedgeTrimmerFaqs && (
            <>
              <section className="mt-16">
                <h2 className="text-2xl font-bold text-foreground mb-4">
                  Which type of hedge trimmer is right for you?
                </h2>
                <p className="text-muted-foreground mb-6">
                  Hedge trimmers fall into four broad types. Each suits a different garden size, hedge type and budget — here's how they compare at a glance.
                </p>
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr className="text-left">
                        <th className="p-3 font-semibold">Type</th>
                        <th className="p-3 font-semibold">Best for</th>
                        <th className="p-3 font-semibold">Typical price</th>
                        <th className="p-3 font-semibold">Weight</th>
                        <th className="p-3 font-semibold">Trade-off</th>
                      </tr>
                    </thead>
                    <tbody className="[&>tr:nth-child(even)]:bg-muted/20">
                      <tr>
                        <td className="p-3 font-semibold">Corded electric</td>
                        <td className="p-3">Small gardens near a socket</td>
                        <td className="p-3">£30–£80</td>
                        <td className="p-3">2.5–3.5 kg</td>
                        <td className="p-3">Cable limits reach</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-semibold">Cordless (18V–40V)</td>
                        <td className="p-3">Most UK domestic hedges</td>
                        <td className="p-3">£80–£220</td>
                        <td className="p-3">2.5–3.8 kg</td>
                        <td className="p-3">Runtime capped by battery</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-semibold">Petrol</td>
                        <td className="p-3">Long or thick hedges, rural plots</td>
                        <td className="p-3">£150–£400+</td>
                        <td className="p-3">4.5–6 kg</td>
                        <td className="p-3">Louder, heavier, fuel mix</td>
                      </tr>
                      <tr>
                        <td className="p-3 font-semibold">Long-reach / telescopic</td>
                        <td className="p-3">Hedges over 2m tall</td>
                        <td className="p-3">£90–£300</td>
                        <td className="p-3">3.5–5.5 kg</td>
                        <td className="p-3">Front-heavy at full extension</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Prices are typical UK RRPs from Amazon, Screwfix and B&amp;Q as of {new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}.
                </p>
              </section>

              <section className="mt-16">
                <h2 className="text-2xl font-bold text-foreground mb-6">
                  Hedge trimmer buying FAQs
                </h2>
                <div className="space-y-6">
                  {hedgeTrimmerFaqs.map((f) => (
                    <div key={f.q} className="border-l-4 border-primary/40 pl-4">
                      <h3 className="font-semibold text-foreground mb-1">{f.q}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{f.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}
        </main>


        {/* Top products from the catalogue (only renders if data exists) */}
        <HubTopProducts hubSlug={config.slug} hubTitle={config.title} />

        <Footer />
      </div>
    </>
  );
};

export default ProductHub;
